import React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Button, Card, CardMedia, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { basic } from '../../../request/values';
import { COLORS } from '../../../shared/Theme';
import { formatMoneyAmount, getTranslatedLicenseName } from '../../../locales/formatModule';

// 테마 컬러 상수화
const THEME = {
	WATER_BLUE: '#1291d9',
	PUMPKIN: '#ef7d00',
	BACKGROUND: '#f4f6f8', // 내부 요소용 옅은 회색 (확실한 구분감)
	SURFACE: '#ffffff',
	BORDER: '#d2d8de', // 테두리 색상
	TEXT_MAIN: '#222222',
	TEXT_SUB: '#888888',
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		card: {
			width: '100%',
			backgroundColor: THEME.SURFACE,
			border: `1px solid ${THEME.BORDER}`, // 흰색 배경과 확실히 구분되는 테두리
			borderRadius: 16,
			boxShadow: '0 2px 8px rgba(0,0,0,0.04)', // 은은하고 얕은 그림자
			padding: theme.spacing(2), // 여백 압축
			display: 'flex',
			flexDirection: 'column',
			gap: theme.spacing(1.2), // 요소 간격 압축
			transition: 'transform 0.2s, box-shadow 0.2s',
			'&:hover': {
				transform: 'translateY(-2px)',
				boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
				borderColor: '#b9c3ce',
			},
		},
		// --- 카드 상단 (레벨 뱃지 및 타이틀) ---
		headerSection: {
			display: 'flex',
			alignItems: 'center', // 가로 배치로 공간 절약
			gap: theme.spacing(1),
		},
		levelBadge: {
			backgroundColor: THEME.PUMPKIN,
			color: '#fff',
			padding: '2px 8px',
			borderRadius: 8,
			fontWeight: 800,
			fontSize: '0.75rem',
			whiteSpace: 'nowrap',
		},
		headerTitle: {
			fontWeight: 800,
			fontSize: '1.05rem', // 폰트 크기 압축
			color: THEME.TEXT_MAIN,
			fontFamily: 'NanumB, sans-serif',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		// --- 카드 이미지 ---
		mediaContainer: {
			backgroundColor: 'white', // 옅은 회색으로 칠해 구분감 부여
			borderRadius: 12,
			padding: theme.spacing(1),
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			border: `1px solid #e2e7ec`, // 내부 요소에도 얇은 선 추가
		},
		media: {
			height: 100, // 이미지 높이 대폭 축소
			width: '100%',
			backgroundSize: 'contain',
			backgroundPosition: 'center',
		},
		// --- 라이선스 가격 (Subtle UI) ---
		licenseRow: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			padding: '0 4px',
			marginTop: 2,
		},
		licenseLabel: {
			fontSize: '0.75rem',
			color: THEME.TEXT_SUB,
			fontWeight: 700,
		},
		licenseValue: {
			fontSize: '0.8rem',
			color: THEME.TEXT_SUB,
			fontWeight: 800,
			fontFamily: 'NanumB, sans-serif',
		},
		// --- 가격 정보 박스 (메인) ---
		priceBox: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			backgroundColor: 'white', // 옅은 회색 배경
			borderRadius: 12,
			padding: theme.spacing(1.2, 1.5),
			border: `1px solid #e2e7ec`,
		},
		priceItem: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			flex: 1,
		},
		priceDivider: {
			width: 1,
			height: 24,
			backgroundColor: '#ccd3db',
		},
		priceLabel: {
			fontSize: '0.75rem',
			color: THEME.TEXT_SUB,
			fontWeight: 700,
			marginBottom: 2,
		},
		priceValue: {
			fontSize: '0.95rem', // 금액 폰트 크기 압축
			color: THEME.WATER_BLUE,
			fontWeight: 800,
			fontFamily: 'NanumB, sans-serif',
		},
		// --- 하단 액션 버튼 ---
		buttonGroup: {
			display: 'flex',
			gap: theme.spacing(1),
			marginTop: 4, // 상단 마진 살짝 추가
		},
		actionButton: {
			flex: 1,
			height: 38, // 버튼 높이 압축
			borderRadius: 8,
			fontWeight: 800,
			fontSize: '0.85rem',
			fontFamily: 'NanumB, sans-serif',
			boxShadow: 'none',
			textTransform: 'none',
			padding: 0,
		},
		btnUIBlue: {
			backgroundColor: THEME.WATER_BLUE,
			color: '#fff',
			'&:hover': {
				backgroundColor: '#0f7bbd',
				boxShadow: 'none',
			},
		},
	}),
);

// 라이선스 가격(licensePrice) 속성 추가
interface iProductItem {
	licenseNum: number;
	productNum: number;
	productName: string;
	productImage: string;
	productMenualFile: string;
	productPrice: number;
	materialPrice: number;
	licensePrice: number; // 새로 추가된 부분
	productRegDate: string;
	productLicenseLevel: number;
	history: any;
	handleHelpClick: any;
	member: any;
}

const ProductItem: React.FC<iProductItem> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation(['object', 'student']);
	const { productName, productImage, productMenualFile, productPrice, productLicenseLevel, materialPrice, licensePrice, history } = props;

	const licenseName = getTranslatedLicenseName(t, productName);

	const goMenual = () => {
		let menualFile = productMenualFile;
		if (props.member.classVersion.toString() === '2' || props.member.classVersion.toString() === '4') {
			menualFile = productMenualFile.replace('.pdf', '2.pdf');
		}
		history.push({
			pathname: `/LicenseManual`,
			state: { productMenualFile: menualFile },
		});
	};

	return (
		<Card className={classes.card}>
			{/* 1. 헤더 영역: 뱃지와 상품명 */}
			<div className={classes.headerSection}>
				<div className={classes.levelBadge}>Lv. {productLicenseLevel}</div>
				<Typography className={classes.headerTitle} title={licenseName}>
					{licenseName}
				</Typography>
			</div>

			{/* 3. 라이선스 가격 (은은하게 배치) */}
			<div className={classes.licenseRow}>
				<Typography className={classes.licenseLabel}>
					{/* 필요 시 i18n 적용: t('student:ProductionItem.licenseValue', '보유 라이선스 가치') */}
					제품 라이선스 가치
				</Typography>
				<Typography className={classes.licenseValue}>{formatMoneyAmount(licensePrice)}</Typography>
			</div>

			{/* 2. 상품 이미지 영역 (옅은 회색 테두리 박스) */}
			<div className={classes.mediaContainer}>
				<CardMedia className={classes.media} image={basic.server + '/displayImage?fileName=' + productImage} />
			</div>

			{/* 4. 가격 정보 영역 (메인) */}
			<div className={classes.priceBox}>
				<div className={classes.priceItem}>
					<Typography className={classes.priceLabel}>{t('student:ProductionItem.materialPrice')}</Typography>
					<Typography className={classes.priceValue}>{formatMoneyAmount(materialPrice)}</Typography>
				</div>
				<div className={classes.priceDivider} />
				<div className={classes.priceItem}>
					<Typography className={classes.priceLabel}>{t('student:ProductionItem.price')}</Typography>
					<Typography className={classes.priceValue}>{formatMoneyAmount(productPrice)}</Typography>
				</div>
			</div>

			{/* 5. 액션 버튼 영역 */}
			<div className={classes.buttonGroup}>
				<Button onClick={goMenual} variant="contained" className={clsx(classes.actionButton, classes.btnUIBlue)} data-testid={`manu-product-manual-btn-${props.licenseNum}`}>
					{t('student:ProductionItem.manual')}
				</Button>

				<Button
					onClick={props.handleHelpClick}
					variant="contained"
					className={clsx(classes.actionButton, classes.btnUIBlue)}
					data-testid={`manu-product-material-list-btn-${props.licenseNum}`}
				>
					{t('student:ProductionItem.materialList')}
				</Button>
			</div>
		</Card>
	);
};

export default ProductItem;
